const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const isDateOnlyAdded = (iso: string): boolean => DATE_ONLY_RE.test(iso);

/**
 * Date-only catalog drops stamp at local noon, which can sort *above* an earlier
 * same-day claim. Slide the drop to just before that event only in that case.
 * Timed `dateAdded` that already precedes the claim stays put so contribution
 * awards (Badger / Baron) are not dragged onto the claim card.
 */
export const catalogMsNudgedBelowEarlierSameDayClaim = (
  catalogMs: number,
  firstSameDayPersonEventMs: number | null
): number => {
  if (firstSameDayPersonEventMs == null || catalogMs <= firstSameDayPersonEventMs) return catalogMs;
  return firstSameDayPersonEventMs - 1;
};

/** Parse catalog `dateAdded`. Date-only (`YYYY-MM-DD`) is local noon; a time uses that instant. */
export const parseDateAdded = (iso: string): Date => {
  const dateOnly = iso.match(DATE_ONLY_RE);
  if (dateOnly) {
    const y = Number(dateOnly[1]);
    const m = Number(dateOnly[2]);
    const d = Number(dateOnly[3]);
    return new Date(y, m - 1, d, 12, 0, 0);
  }
  return new Date(iso);
};

/** Catalog date when a badge has no explicit `dateAdded`. */
export const DEFAULT_BADGE_DATE_ADDED = '2024-03-15';

/** Lock-line label: date-only stays `YYYY-MM-DD`; timed values show local `YYYY-MM-DD HH:mm`. */
export const formatDateAddedLockLabel = (iso: string): string => {
  if (isDateOnlyAdded(iso)) return iso;
  const d = parseDateAdded(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** True when a catalog `dateAdded` has been reached. Date-only: that local calendar day. Timed: that instant. */
export const isDateAddedVisible = (iso: string, now = new Date()): boolean => {
  if (!isDateOnlyAdded(iso)) return now.getTime() >= parseDateAdded(iso).getTime();
  const [y, m, d] = iso.split('-').map(Number);
  const addedDay = new Date(y, m - 1, d).getTime();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return addedDay <= startOfToday;
};
